/**
 * What a player sees when they open their link.
 *
 * No account, no dashboard, no campaign — one board and one token. The seat
 * key in the URL is the whole credential, which is why this page never asks
 * the auth provider for anything: it has to work for someone who has never
 * heard of Throughline and was handed a link in a Discord call.
 *
 * Polls rather than streams. A battle map is a handful of squares changing a
 * few times a minute, the server answers an unchanged poll with two fields,
 * and a poll cannot silently die the way a socket can — which, in this app,
 * is not a hypothetical.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import type { MapState } from '@/api/battlemap';
import { fetchSeat, moveSeatToken, SeatFetchError } from '@/api/battlemap';
import { MapCanvas } from '@/components/MapCanvas';

const POLL_MS = 1_500;

export function PlayerMap({ seatKey, presentation }: {
  seatKey: string;
  /** Frame-driven tutorial state; no seat requests or writes are made. */
  presentation?: { state: MapState; label: string; tokenId: string | null; saving?: boolean };
}) {
  const [localState, setState] = useState<MapState | null>(null);
  const state = presentation?.state ?? localState;
  const [localLabel, setLabel] = useState('');
  const label = presentation?.label ?? localLabel;
  const [localTokenId, setTokenId] = useState<string | null>(null);
  const tokenId = presentation ? presentation.tokenId : localTokenId;
  const [localWaiting, setWaiting] = useState(true);
  const waiting = presentation ? false : localWaiting;
  const [invalid, setInvalid] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const revision = useRef(-1);
  const moving = useRef(false);
  const [localSaving, setSaving] = useState(false);
  const saving = presentation?.saving ?? localSaving;
  const requestGeneration = useRef(0);
  const pollPending = useRef(false);
  // A poll landing mid-drag would yank the token back under the player's
  // finger; the local move stands until the next poll confirms it.
  const holdUntil = useRef(0);

  const poll = useCallback(
    async (force = false) => {
      if (
        !force &&
        (invalid || moving.current || pollPending.current || document.hidden)
      )
        return;
      const generation = requestGeneration.current;
      pollPending.current = true;
      try {
        const view = await fetchSeat(seatKey, force ? -1 : revision.current);
        if (
          generation !== requestGeneration.current ||
          (!force && moving.current)
        )
          return;
        setError(null);
        if (view.unchanged || view.revision < revision.current) return true;
        revision.current = view.revision;
        if (view.label !== undefined) setLabel(view.label);
        if (view.token_id !== undefined) setTokenId(view.token_id);
        setWaiting(Boolean(view.waiting));
        if (view.state && (force || Date.now() > holdUntil.current)) {
          setState(view.state);
        }
        return true;
      } catch (err) {
        if (generation !== requestGeneration.current) return;
        if (err instanceof SeatFetchError && err.status === 404) {
          setInvalid(true);
          setWaiting(false);
          setState(null);
        }
        setError(
          err instanceof SeatFetchError
            ? err.message
            : 'Connection interrupted. Your board is still here; reconnecting…',
        );
        return false;
      } finally {
        pollPending.current = false;
      }
    },
    [invalid, seatKey],
  );

  useEffect(() => {
    if (presentation) return;
    void poll();
    const timer = window.setInterval(() => void poll(), POLL_MS);
    const onVisible = () => {
      if (!document.hidden) void poll();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [poll, presentation]);

  async function handleMove(id: string, x: number, y: number) {
    if (presentation || id !== tokenId || !state || moving.current) return;
    const token = state.tokens.find((t) => t.id === id);
    if (!token || (token.x === x && token.y === y)) return;
    moving.current = true;
    requestGeneration.current += 1;
    setSaving(true);
    holdUntil.current = Date.now() + 2_000;
    setState({
      ...state,
      tokens: state.tokens.map((t) => (t.id === id ? { ...t, x, y } : t)),
    });
    try {
      const result = await moveSeatToken(seatKey, id, x, y);
      revision.current = result.revision;
      holdUntil.current = 0;
      setState(result.state);
      setError(null);
    } catch {
      holdUntil.current = 0;
      revision.current = -1;
      setState(state);
      const restored = await poll(true);
      setError(
        restored
          ? 'Your move was not saved. The board has been restored.'
          : 'Your move was not saved. Showing the previous position while we reconnect.',
      );
    } finally {
      moving.current = false;
      setSaving(false);
    }
  }

  return (
    <div className="playermap">
      <header className="playermap__head">
        <div>
          <p className="desk-eyebrow">Throughline · Shared combat board</p>
          <h1 className="playermap__title">
            {label || 'Your seat at the table'}
          </h1>
        </div>
        <span className="playermap__hint">
          {waiting
            ? 'Waiting for your GM.'
            : tokenId && state
              ? 'Drag your token or use the arrow keys to move.'
              : 'Spectator view · follow the action.'}
        </span>
      </header>

      {error ? (
        <div className="notice notice--error" role="alert">
          {error}
          {!invalid ? (
            <button
              type="button"
              className="button button--small"
              disabled={saving}
              onClick={() => void poll(true)}
            >
              Reconnect
            </button>
          ) : (
            <p>Ask your GM for a new player link.</p>
          )}
        </div>
      ) : null}

      {invalid ? null : waiting || !state || !state.width ? (
        <p className="playermap__waiting">
          <span className="spinner" aria-hidden="true" />
          Waiting for your GM to publish the map.
        </p>
      ) : (
        <>
          <MapCanvas
            state={state}
            draggableIds={tokenId && !saving ? [tokenId] : []}
            selectedId={tokenId}
            onMove={(id, x, y) => void handleMove(id, x, y)}
          />
          <div className="playermap__connection" role="status">
            {saving
              ? 'Saving your move…'
              : error
                ? 'Reconnecting…'
                : 'Board connected'}
          </div>
          {tokenId && state.tokens.some((t) => t.id === tokenId) ? (
            <div
              className="playermap__controls"
              role="group"
              aria-label="Move your character"
            >
              <span>Move your character</span>
              {(
                [
                  ['←', -1, 0, 'left'],
                  ['↑', 0, -1, 'up'],
                  ['↓', 0, 1, 'down'],
                  ['→', 1, 0, 'right'],
                ] as const
              ).map(([symbol, dx, dy, direction]) => {
                const token = state.tokens.find((t) => t.id === tokenId)!;
                const x = Math.max(0, Math.min(state.width - 1, token.x + dx));
                const y = Math.max(0, Math.min(state.height - 1, token.y + dy));
                return (
                  <button
                    type="button"
                    key={direction}
                    className="button"
                    aria-label={`Move ${direction}`}
                    disabled={saving || (x === token.x && y === token.y)}
                    onClick={() => void handleMove(tokenId, x, y)}
                  >
                    {symbol}
                  </button>
                );
              })}
            </div>
          ) : null}
          {state.notes ? (
            <p className="playermap__notes">{state.notes}</p>
          ) : null}
          <p className="playermap__legend">
            Select a numbered terrain area or its description to read what it
            does.
          </p>
        </>
      )}
    </div>
  );
}
